import React, { useState, useEffect } from 'react';
import { array, bool, number } from 'prop-types';
import { get, isEmpty } from 'lodash';

import MaterialTable from 'material-table';

import { TablePagination } from '@material-ui/core';

import ActionBar from '../../../components/ActionBar';
import ActionBarItem from '../../../components/ActionBarItem';
import Switch from '../../../components/FormFields/Switch';

import classes from './Table.module.css';

const ServiceStatusSettingsTable = ({
    serviceStatusSettings,
    serviceStatusSettingsMeta,
    isLoading,
    setServiceStatusSettingsSearch,
    onPageSet,
    onPageSizeSet,
    page,
    pageSize,
    updateServiceStatusSettings,
    ...props
}) => {

    const [filteredServiceStatusSettings, setFilteredServiceStatusSettings] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [commToggles, setCommToggles] = useState({});
    const [confirmToggles, setConfirmToggles] = useState({});
    const [alertToggles, setAlertToggles] = useState({});
    const [totalCount, setTotalCount] = useState(0);

    useEffect(() => {
        setFilteredServiceStatusSettings(serviceStatusSettings);

        serviceStatusSettings.forEach(serviceStatus => {
            setCommToggles({...commToggles, [serviceStatus.id]: false});
            setConfirmToggles({...confirmToggles, [serviceStatus.id]: false});
            setAlertToggles({...alertToggles, [serviceStatus.id]: false});
        });
    }, [serviceStatusSettings]);

    useEffect(() => {
        if (serviceStatusSettingsMeta.totalCount && serviceStatusSettingsMeta.totalCount !== totalCount) {
            setTotalCount(serviceStatusSettingsMeta.totalCount);
        }
    }, [serviceStatusSettingsMeta]);

    const commSorter = (a, b) => {
        return sorter(a, b, 'communicationEnabled');
    };

    const confirmSorter = (a, b) => {
        return sorter(a, b, 'confirmationEnabled');
    };

    const alertSorter = (a, b) => {
        return sorter(a, b, 'alertEnabled');
    };

    const sorter = (a, b, property) => {
        const valueA = a[property];
        const valueB = b[property];

        return (valueA === valueB) ? 0 : valueA ? -1 : 1;
    };

    const handlePageChange = (event, page) => {
        onPageSet(page);
    };

    const handlePageSizeChange = event => {
        const { value } = event.target;

        onPageSizeSet(value);
    };

    const handleSearch = searchValue => {
        if (isEmpty(searchValue)) {
            setFilteredServiceStatusSettings([...serviceStatusSettings]);

            return;
        }

        const results = serviceStatusSettings.filter(settings =>
            settings.status.toLowerCase().includes(searchValue.toLowerCase())
        );

        setFilteredServiceStatusSettings(results);
    };

    const handleSetSelectedServiceStatusSettings = selected => {
        setSelectedStatuses(selected);
    };

    const handleCommToggle = async rowData => {
        const switchStates = {
            communicationEnabled: !rowData.communicationEnabled,
            confirmationEnabled:  rowData.communicationEnabled ? false : rowData.confirmationEnabled,
            alertEnabled:         rowData.alertEnabled
        };

        setCommToggles({...commToggles, [rowData.id]: true});
        await updateServiceStatusSettings(switchStates, rowData);
        setCommToggles({...commToggles, [rowData.id]: false});
    };

    const handleConfirmToggle = async rowData => {
        const switchStates = {
            communicationEnabled: rowData.communicationEnabled,
            confirmationEnabled:  !rowData.confirmationEnabled,
            alertEnabled:         rowData.alertEnabled
        };

        setConfirmToggles({...confirmToggles, [rowData.id]: true});
        await updateServiceStatusSettings(switchStates, rowData);
        setConfirmToggles({...confirmToggles, [rowData.id]: false});
    };

    const handleAlertToggle = async rowData => {
        const switchStates = {
            communicationEnabled: rowData.communicationEnabled,
            confirmationEnabled:  rowData.confirmationEnabled,
            alertEnabled:         !rowData.alertEnabled
        };

        setAlertToggles({...alertToggles, [rowData.id]: true});
        await updateServiceStatusSettings(switchStates, rowData);
        setAlertToggles({...alertToggles, [rowData.id]: false});
    };

    const handleToggleCommunication = isEnabled => {
        if (isEmpty(serviceStatusSettings)) {
            return;
        }

        const newIds = selectedStatuses.map(status => status.id);

        const toggles = {};
        newIds.forEach(id => {
            toggles[id] = true;
        });
        setCommToggles({...commToggles, ...toggles});

        const promises = [];
        selectedStatuses.forEach(status => {
            let switchStates = {
                communicationEnabled: isEnabled,
                confirmationEnabled:  status.confirmationEnabled,
                alertEnabled:         status.alertEnabled
            };

            promises.push(updateServiceStatusSettings(switchStates, status));
        });

        Promise.all(promises).then(() => {
            newIds.forEach(id => {
                toggles[id] = false;
            });
            setCommToggles({...commToggles, ...toggles});
        });
    };

    const handleToggleConfirmation = isEnabled => {
        if (isEmpty(serviceStatusSettings)) {
            return;
        }

        const newIds = selectedStatuses.map(status => status.id);

        const toggles = {};
        newIds.forEach(id => {
            toggles[id] = true;
        });
        setConfirmToggles({...confirmToggles, ...toggles});

        const promises = [];
        selectedStatuses.forEach(status => {
            let switchStates = {
                communicationEnabled: status.communicationEnabled,
                confirmationEnabled:  isEnabled,
                alertEnabled:         status.alertEnabled
            };

            promises.push(updateServiceStatusSettings(switchStates, status));
        });

        Promise.all(promises).then(() => {
            newIds.forEach(id => {
                toggles[id] = false;
            });
            setConfirmToggles({...confirmToggles, ...toggles});
        });
    };

    const renderCommToggleCell = (rowData, value) => {
        if (rowData.loadFailed) {
            return (
                <span className={classes.error}>{rowData[value]}</span>
            );
        }

        return (
            <Switch
                checked={rowData[value] || false}
                disableRipple
                name="enabled"
                onChange={() => handleCommToggle(rowData)}
                isLoading={commToggles[rowData.id]}
            />
        );
    };

    const renderRequireConfirmToggleCell = (rowData, value) => {
        if (rowData.loadFailed) {
            return (
                <span className={classes.error}>{rowData[value]}</span>
            );
        }

        return (
            <Switch
                checked={rowData[value] || false}
                disableRipple
                disabled={!rowData.communicationEnabled}
                name="enabled"
                onChange={() => handleConfirmToggle(rowData)}
                isLoading={confirmToggles[rowData.id]}
            />
        );
    };

    const renderAlertTechToggleCell = (rowData, value) => {
        if (rowData.loadFailed) {
            return (
                <span className={classes.error}>{rowData[value]}</span>
            );
        }

        const entity = get(rowData, 'entity');
        const status = get(rowData, 'status');

        const isHidden = entity.toLowerCase() === 'service call' ||
                         (status.toLowerCase() !== 'canceled' &&
                         status.toLowerCase() !== 'rescheduled' &&
                         status.toLowerCase() !== 'scheduled');

        if (isHidden) return null;

        return (
            <Switch
                checked={rowData[value] || false}
                disableRipple
                name="enabled"
                onChange={() => handleAlertToggle(rowData)}
                isLoading={alertToggles[rowData.id]}
            />
        );
    };

    return (
        <React.Fragment>
            <div className={classes.Table} >
                <ActionBar
                    onSearch={handleSearch}
                >
                    <ActionBarItem
                        onClick={() => handleToggleCommunication(true)}
                        iconSrc="/assets/images/switchEnabledIconColor.svg"
                    >
                        Enabled Communication
                    </ActionBarItem>
                    <ActionBarItem
                        onClick={() => handleToggleCommunication(false)}
                        iconSrc="/assets/images/switchDisabledIconColor.svg"
                    >
                        Disabled Communication
                    </ActionBarItem>
                    <ActionBarItem
                        onClick={() => handleToggleConfirmation(true)}
                        iconSrc="/assets/images/switchEnabledIconColor.svg"
                    >
                        Enabled Confirmation
                    </ActionBarItem>
                    <ActionBarItem
                        onClick={() => handleToggleConfirmation(false)}
                        iconSrc="/assets/images/switchDisabledIconColor.svg"
                    >
                        Disabled Confirmation
                    </ActionBarItem>
                </ActionBar>
                <MaterialTable
                    options={{
                        paging:              true,
                        showTitle:           false,
                        search:              false,
                        selection:           true,
                        sorting:             true,
                        toolbar:             false,
                        emptyRowsWhenPaging: false,
                        pageSize
                    }}
                    columns={[
                        {
                            title:       'Service Entity',
                            field:       'entity',
                            defaultSort: 'desc'
                        },
                        {
                            title: 'Status',
                            field: 'status'
                        },
                        {
                            title:      'Enabled Communication',
                            render:     rowData => renderCommToggleCell(rowData, 'communicationEnabled'),
                            customSort: commSorter
                        },
                        {
                            title:      'Require Confirmation Before Sending',
                            render:     rowData => renderRequireConfirmToggleCell(rowData, 'confirmationEnabled'),
                            customSort: confirmSorter
                        },
                        {
                            title:      'Alert Technician Via SMS',
                            render:     rowData => renderAlertTechToggleCell(rowData, 'alertEnabled'),
                            customSort: alertSorter
                        }
                    ]}
                    data={filteredServiceStatusSettings}
                    isLoading={isLoading}
                    onSelectionChange={handleSetSelectedServiceStatusSettings}
                    components={{
                        Pagination: props => {
                            return (
                                <TablePagination
                                    {...props}
                                    count={totalCount}
                                    page={serviceStatusSettings.length ? page : 0}
                                    rowsPerPage={pageSize}
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={event => { props.onChangeRowsPerPage(event); handlePageSizeChange(event); } }
                                />
                            );
                        }
                    }}
                />
            </div>
        </React.Fragment>
    );
};

ServiceStatusSettingsTable.defaultProps = {
    serviceStatusSettings:        [],
    serviceStatusSettingsLoading: false,
    page:                         0,
    pageSize:                     100
};

ServiceStatusSettingsTable.propTypes = {
    serviceStatusSettings:        array.isRequired,
    serviceStatusSettingsLoading: bool,
    page:                         number,
    pageSize:                     number
};

export default ServiceStatusSettingsTable;
